// countup_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["output"]
  static values = {
    start: { type: Number, default: 0 },
    end: { type: Number, default: 100 },
    duration: { type: Number, default: 2000 },
    decimals: { type: Number, default: 0 },
    prefix: { type: String, default: "" },
    suffix: { type: String, default: "" },
    separator: { type: String, default: "," },
    decimal: { type: String, default: "." },
    easing: { type: String, default: "easeOutExpo" },
    startOnVisible: { type: Boolean, default: true }
  }

  connect() {
    // Initialize the counter
    this.currentValue = this.startValue
    this.outputTarget.textContent = this.formatNumber(this.currentValue)

    // Set up the Intersection Observer to detect when element becomes visible
    this.setupIntersectionObserver()
  }

  setupIntersectionObserver() {
    // Create options for the observer
    const options = {
      root: null, // viewport is used as the root
      rootMargin: '0px',
      threshold: 0.1 // trigger when at least 10% of the element is visible
    }

    // Create the observer
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasStarted) {
          // Element is now visible, start counting
          this.startCounting()
          this.hasStarted = true

          // Optionally disconnect the observer after animation has started
          // Comment out the next line if you want it to restart when scrolling back into view
          this.observer.disconnect()
        }
      })
    }, options)

    // Start observing the element
    this.observer.observe(this.element)
  }

  disconnect() {
    // Clean up the observer when the controller is disconnected
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  startCounting() {
    const startTime = performance.now()
    const endTime = startTime + this.durationValue

    // Use requestAnimationFrame for smooth animation
    const updateCounter = (currentTime) => {
      if (currentTime >= endTime) {
        // Animation completed
        this.currentValue = this.endValue
        this.outputTarget.textContent = this.formatNumber(this.currentValue)
        return
      }

      // Calculate progress based on timing and easing
      const elapsed = currentTime - startTime
      const progress = this.getEasedProgress(elapsed / this.durationValue)

      // Update current value
      this.currentValue = this.startValue + (this.endValue - this.startValue) * progress

      // Update the output display
      this.outputTarget.textContent = this.formatNumber(this.currentValue)

      // Continue animation
      window.requestAnimationFrame(updateCounter)
    }

    window.requestAnimationFrame(updateCounter)
  }

  // Format number with proper decimals, prefix, suffix, and separators
  formatNumber(value) {
    // Round to specified decimal places
    const roundedValue = parseFloat(value.toFixed(this.decimalsValue))

    // Convert to string with proper decimal places
    let formattedNumber = roundedValue.toLocaleString(undefined, {
      minimumFractionDigits: this.decimalsValue,
      maximumFractionDigits: this.decimalsValue,
      useGrouping: true
    })

    // Replace default separator and decimal if custom ones are specified
    if (this.separatorValue !== ",") {
      formattedNumber = formattedNumber.replace(/,/g, this.separatorValue)
    }

    if (this.decimalValue !== "." && this.decimalsValue > 0) {
      formattedNumber = formattedNumber.replace(".", this.decimalValue)
    }

    // Add prefix and suffix
    return `${this.prefixValue}${formattedNumber}${this.suffixValue}`
  }

  // Easing functions for smoother animation
  getEasedProgress(t) {
    switch (this.easingValue) {
      case "linear":
        return t
      case "easeInQuad":
        return t * t
      case "easeOutQuad":
        return t * (2 - t)
      case "easeInOutQuad":
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
      case "easeInCubic":
        return t * t * t
      case "easeOutCubic":
        return (--t) * t * t + 1
      case "easeInOutCubic":
        return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1
      case "easeOutExpo":
        return t === 1 ? 1 : 1 - Math.pow(2, -10 * t)
      default:
        return t
    }
  }
}
